import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCreditCard, FiChevronLeft, FiChevronRight, FiLock, FiZap, FiSmartphone, FiCheck, FiInfo } from 'react-icons/fi';
import { SectionHeader } from '../../../shared/TitleHeader';
import style from './style.module.scss';
import visaLogo from '../../../../assets/images/visa.png';
import mastercardLogo from '../../../../assets/images/mastercard.png';
import iziLogo from '../../../../assets/images/Izi-logo.png';

export const VirtualCardSection = () => {
    const { t } = useTranslation('translations');
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [isHovering, setIsHovering] = useState(false);
    const autoplayTimerRef = useRef(null);

    // Card data
    const cards = [
        {
            type: 'VISA',
            brand: 'VISA',
            gradient: 'linear-gradient(135deg, #23253D 0%, #008080 100%)',
            logo: visaLogo,
        },
        {
            type: 'MASTERCARD',
            brand: 'Mastercard',
            gradient: 'linear-gradient(135deg, #23253D 0%, #3257A8 100%)',
            logo: mastercardLogo,
        },
    ];

    // Advantages carousel data
    const advantages = [
        {
            id: 1,
            icon: <FiLock size={32} />,
            title: t('landing.virtualCard.advantages.security.title'),
            description: t('landing.virtualCard.advantages.security.description'),
        },
        {
            id: 2,
            icon: <FiZap size={32} />,
            title: t('landing.virtualCard.advantages.instant.title'),
            description: t('landing.virtualCard.advantages.instant.description'),
        },
        {
            id: 3,
            icon: <FiSmartphone size={32} />,
            title: t('landing.virtualCard.advantages.mobile.title'),
            description: t('landing.virtualCard.advantages.mobile.description'),
        },
        {
            id: 4,
            icon: <FiCreditCard size={32} />,
            title: t('landing.virtualCard.advantages.multicard.title'),
            description: t('landing.virtualCard.advantages.multicard.description'),
        },
    ];

    const handlePrevious = () => {
        setCarouselIndex((prev) =>
            prev === 0 ? advantages.length - 1 : prev - 1
        );
    };

    const handleNext = () => {
        setCarouselIndex((prev) =>
            prev === advantages.length - 1 ? 0 : prev + 1
        );
    };

    // Autoplay effect
    useEffect(() => {
        if (isHovering) {
            // Pause autoplay on hover
            if (autoplayTimerRef.current) {
                clearInterval(autoplayTimerRef.current);
                autoplayTimerRef.current = null;
            }
        } else {
            // Resume autoplay when not hovering
            autoplayTimerRef.current = setInterval(() => {
                setCarouselIndex((prev) =>
                    prev === advantages.length - 1 ? 0 : prev + 1
                );
            }, 4000);
        }

        return () => {
            if (autoplayTimerRef.current) {
                clearInterval(autoplayTimerRef.current);
            }
        };
    }, [isHovering, advantages.length]);

    const handleMouseEnter = () => {
        setIsHovering(true);
    };

    const handleMouseLeave = () => {
        setIsHovering(false);
    };

    return (
        <div className={style.sectionWrapper}>
            <div className="container">
                {/* ===== HEADER BLOCK ===== */}
                <div className={style.headerBlock}>
                    <SectionHeader
                        position="center"
                        subtitle={t('landing.virtualCard.subtitle')}
                        title={t('landing.virtualCard.title')}
                        description={t('landing.virtualCard.description')}
                    />

                    {/* Badges Row (part of header) */}
                    <div className={style.badgesRow}>
                        <div className={style.badge}>{t('landing.virtualCard.badges.deposit')}</div>
                        <div className={style.badge}>{t('landing.virtualCard.badges.limit')}</div>
                    </div>
                </div>

                {/* ===== PRODUCT BLOCK ===== */}
                <div className={style.productBlock}>
                    <div className={style.cardsContainer}>
                        {cards.map((card) => (
                            <div key={card.type} className={style.cardWrapper}>
                                <div
                                    className={style.card}
                                    style={{ background: card.gradient }}
                                >
                                    {/* Top Section: Balance (left) & Brand Logo (right) */}
                                    <div className={style.cardTopSection}>
                                        <div className={style.balanceBlock}>
                                            <div className={style.balanceLabel}>Balance</div>
                                            <div className={style.balanceMasked}>••••</div>
                                        </div>
                                        <img 
                                            src={card.logo}
                                            alt={card.brand}
                                            className={style.brandLogo}
                                        />
                                    </div>

                                    {/* Middle Section: Card Number (left) & Details Button (right) */}
                                    <div className={style.cardMiddleSection}>
                                        <div className={style.cardNumber}>
                                            •••• •••• •••• <span className={style.lastDigits}>••••</span>
                                        </div>
                                        <button className={style.detailsBtn}>
                                            <FiInfo size={14} />
                                            Details
                                        </button>
                                    </div>

                                    {/* Bottom Section: Name (left) & CVV/Expiration/Izi Logo (right) */}
                                    <div className={style.cardBottomSection}>
                                        <div className={style.nameBlock}>
                                            <div className={style.infoLabel}>Name</div>
                                            <div className={style.infoValue}>•••••••</div>
                                        </div>
                                        
                                        <div className={style.bottomRightBlock}>
                                            <div className={style.detailItem}>
                                                <span className={style.detailLabel}>CVV</span>
                                                <span className={style.detailValue}>•••</span>
                                            </div>
                                            <div className={style.detailItem}>
                                                <span className={style.detailLabel}>Expiration</span>
                                                <span className={style.detailValue}>••/••</span>
                                            </div>
                                            <img 
                                                src={iziLogo}
                                                alt="Izi"
                                                className={style.iziLogo}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ===== PRIMARY ACTION BLOCK ===== */}
                <div className={style.primaryActionBlock}>
                    <a
                        className={style.ctaButton}
                        href="https://dashboard.izichange.com/virtual-card"
                        target="_blank"
                        rel="noreferrer"
                    >
                        {t('landing.virtualCard.cta')}
                    </a>
                </div>

                {/* ===== ADVANTAGES CAROUSEL ===== */}
                <div className={style.advantagesSection}>
                    <h3 className={style.advantagesTitle}>
                        {t('landing.virtualCard.advantages.title')}
                    </h3>

                    <div className={style.carouselWrapper} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                        <button
                            className={style.carouselButton}
                            onClick={handlePrevious}
                            aria-label="Previous advantage"
                        >
                            <FiChevronLeft size={24} />
                        </button>

                        <div className={style.carouselContainer}>
                            {advantages.map((advantage, index) => {
                                // Calculate position relative to center
                                const totalItems = advantages.length;
                                let position = index - carouselIndex;
                                
                                // Normalize position for infinite loop (-2, -1, 0, 1)
                                if (position < -1) position += totalItems;
                                if (position > 2) position -= totalItems;
                                
                                // Determine card state
                                const isCenter = position === 0;
                                const isLeft = position === -1;
                                const isRight = position === 1;
                                const isHidden = position === 2 || position < -1;
                                
                                let cardClass = style.advantageCard;
                                if (isCenter) cardClass += ` ${style.center}`;
                                if (isLeft) cardClass += ` ${style.left}`;
                                if (isRight) cardClass += ` ${style.right}`;
                                if (isHidden) cardClass += ` ${style.hidden}`;
                                
                                const handleCardClick = () => {
                                    if (isLeft) {
                                        setCarouselIndex(index);
                                    } else if (isRight) {
                                        setCarouselIndex(index);
                                    }
                                };
                                
                                return (
                                    <div
                                        key={advantage.id}
                                        className={cardClass}
                                        onClick={handleCardClick}
                                        style={{ cursor: (isLeft || isRight) ? 'pointer' : 'default' }}
                                    >
                                        <div className={style.iconWrapper}>
                                            {advantage.icon}
                                        </div>
                                        <h4 className={style.advantageTitle}>
                                            {advantage.title}
                                        </h4>
                                        <p className={style.advantageDescription}>
                                            {advantage.description}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>

                        <button
                            className={style.carouselButton}
                            onClick={handleNext}
                            aria-label="Next advantage"
                        >
                            <FiChevronRight size={24} />
                        </button>
                    </div>

                    {/* Carousel Indicators */}
                    <div className={style.indicators}>
                        {advantages.map((_, index) => (
                            <button
                                key={index}
                                className={`${style.indicator} ${
                                    index === carouselIndex ? style.active : ''
                                }`}
                                onClick={() => setCarouselIndex(index)}
                                aria-label={`Go to advantage ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VirtualCardSection;
